import React, { useEffect, useState } from "react";
import { DollarSign, Loader2, CheckCircle2, ExternalLink, Users, TrendingUp } from "lucide-react";
import api from "../lib/api";
import { useNavigate } from "react-router-dom";
import { Avatar } from "./Media";
import { toast } from "sonner";

const COUNTRIES = [["US", "United States"], ["CA", "Canada"], ["GB", "United Kingdom"], ["AU", "Australia"], ["NZ", "New Zealand"], ["IE", "Ireland"],
  ["DE", "Germany"], ["FR", "France"], ["NL", "Netherlands"], ["BE", "Belgium"], ["ES", "Spain"], ["PT", "Portugal"], ["IT", "Italy"], ["AT", "Austria"],
  ["CH", "Switzerland"], ["SE", "Sweden"], ["NO", "Norway"], ["DK", "Denmark"], ["FI", "Finland"], ["PL", "Poland"], ["MX", "Mexico"], ["BR", "Brazil"],
  ["JP", "Japan"], ["SG", "Singapore"], ["HK", "Hong Kong"], ["AE", "United Arab Emirates"]];

// Creator panel: onboard payouts (Stripe Connect, creator is the seller) + set a monthly subscriber price + earnings.
export const BeaconSubscriptions = () => {
  const [st, setSt] = useState(null);
  const [earn, setEarn] = useState(null);
  const navigate = useNavigate();
  const [price, setPrice] = useState("");
  const [busy, setBusy] = useState("");

  const load = async () => {
    try { const { data } = await api.get("/beacon/connect/status"); setSt(data); if (data.price_cents) setPrice((data.price_cents / 100).toString()); }
    catch { /* noop */ }
    try { const { data } = await api.get("/beacon/subscribers/summary"); setEarn(data); }
    catch { /* noop */ }
  };
  useEffect(() => { load(); }, []);

  const [onboardErr, setOnboardErr] = useState("");
  const [country, setCountry] = useState("US");
  // Same-window redirect: popups are unreliable in the installed iOS app; Stripe returns to /beacon.
  const onboard = async () => {
    setBusy("onboard"); setOnboardErr("");
    try { const { data } = await api.post("/beacon/connect/onboard", { origin_url: window.location.origin, country }); window.location.href = data.url; }
    catch (e) { const msg = e?.response?.data?.detail || "Couldn't start payout setup"; setOnboardErr(msg); toast.error(msg); setBusy(""); }
  };

  const savePrice = async () => {
    const cents = Math.round(parseFloat(price) * 100);
    if (!cents || cents < 100) { toast.error("Enter at least $1"); return; }
    setBusy("price");
    try { await api.put("/beacon/subscription-price", { price_cents: cents, enabled: true }); toast.success("Subscriber price saved"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(""); }
  };

  if (!st || !st.has_beacon) return null;

  return (
    <div className="border border-white/10 bg-[#121212] p-5 mb-6" data-testid="beacon-subscriptions">
      <h2 className="font-body text-lg text-white flex items-center gap-2 mb-1"><DollarSign size={17} className="text-[#14A89A]" /> Paid subscriptions</h2>
      <p className="font-body text-xs text-zinc-500 mb-4">Charge a monthly fee for access to your subscriber community. Members pay you directly through your own Stripe account. SovSig takes just a 1% platform fee, and Stripe's standard card fees apply. Set a price and connect payouts to start.</p>

      <div className="space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          {st.ready
            ? <>
                <span className="flex items-center gap-1.5 text-[#14A89A] font-body text-sm" data-testid="connect-ready"><CheckCircle2 size={16} /> Payouts connected</span>
                <a href="https://dashboard.stripe.com" target="_blank" rel="noopener noreferrer" data-testid="connect-dashboard-link" className="font-body text-xs text-zinc-400 hover:text-white underline underline-offset-2">Open my Stripe dashboard</a>
              </>
            : <>
              {!st.onboarded && (
                <select value={country} onChange={(e) => setCountry(e.target.value)} data-testid="connect-country-select" aria-label="Country of your bank account" className="bg-[#1a1a1a] border border-white/12 text-white font-body text-xs px-2 py-2">
                  {COUNTRIES.map(([c, n]) => <option key={c} value={c}>{n}</option>)}
                </select>
              )}
              <button onClick={onboard} disabled={busy === "onboard"} data-testid="connect-onboard" className="flex items-center gap-1.5 bg-[#14A89A] text-[#0A0A0A] font-semibold px-4 py-2 font-body text-xs">
                {busy === "onboard" ? <Loader2 size={13} className="animate-spin" /> : <ExternalLink size={13} />} {st.onboarded ? "Finish payout setup" : "Set up payouts"}
              </button>
              </>}
        </div>
        {onboardErr && <p className="font-body text-xs text-amber-300" data-testid="connect-onboard-error">{onboardErr}</p>}

        <div className="flex items-end gap-2">
          <div>
            <label className="font-body text-[11px] text-zinc-500 block mb-1">Monthly price (USD)</label>
            <div className="flex items-center border border-white/12 bg-[#1a1a1a] px-2">
              <span className="text-zinc-500 font-body text-sm">$</span>
              <input value={price} onChange={(e) => setPrice(e.target.value.replace(/[^0-9.]/g, ""))} data-testid="sub-price-input" placeholder="9" inputMode="decimal" className="w-24 bg-transparent text-white font-body text-sm px-1 py-2 outline-none" />
              <span className="text-zinc-600 font-body text-xs">/mo</span>
            </div>
          </div>
          <button onClick={savePrice} disabled={busy === "price"} data-testid="save-sub-price" className="border border-white/15 text-zinc-200 px-4 py-2 font-body text-xs hover:bg-white/5">{busy === "price" ? <Loader2 size={13} className="animate-spin" /> : "Save price"}</button>
        </div>

        {!st.ready && st.price_cents && <p className="font-body text-[11px] text-amber-400/70" data-testid="connect-hint">{st.onboarded ? "Finish your Stripe verification to start accepting subscribers. It can take a few minutes after you submit." : "Connect payouts to start accepting subscribers. Pick the country where your bank account is, then Stripe walks you through a short verification."}</p>}

        {(st.onboarded || (earn && earn.active_count > 0)) && (
          <button onClick={() => navigate("/beacon/earnings")} data-testid="open-earnings" className="self-start flex items-center gap-1.5 font-body text-xs text-[#14A89A] hover:text-white">
            <TrendingUp size={14} /> View earnings, payouts & subscribers →
          </button>
        )}

        {earn && earn.active_count > 0 && (
          <div className="border-t border-white/10 pt-4 mt-1" data-testid="beacon-earnings-summary">
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div><div className="font-display text-2xl text-white flex items-center gap-1.5"><Users size={16} className="text-[#14A89A]" />{earn.active_count}</div><div className="font-body text-[11px] text-zinc-500">subscriber{earn.active_count === 1 ? "" : "s"}</div></div>
              <div><div className="font-display text-2xl text-white">${(earn.mrr_cents / 100).toFixed(0)}</div><div className="font-body text-[11px] text-zinc-500">monthly revenue</div></div>
              <div><div className="font-display text-2xl text-[#14A89A] flex items-center gap-1"><TrendingUp size={15} />${(earn.net_cents / 100).toFixed(0)}</div><div className="font-body text-[11px] text-zinc-500">your payout (after 1%)</div></div>
            </div>
            <div className="flex -space-x-2">
              {earn.subscribers.slice(0, 12).map((s) => <Avatar key={s.id} name={s.name} src={s.avatar} iv={s.avatar_iv} size={28} />)}
              {earn.active_count > 12 && <span className="font-body text-xs text-zinc-500 ml-4 self-center">+{earn.active_count - 12} more</span>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BeaconSubscriptions;
